import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users2,
  UtensilsCrossed,
  ShoppingBasket,
  BarChart3,
  CalendarClock,
  FileBarChart2,
  Warehouse,
  Settings,
  LogOut,
  Radio,
  Tablet,
  CreditCard,
  Receipt,
  Smartphone,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { useEstablishmentStore } from '@/store/establishment.store';
import { roleLabels } from '@/utils/format';
import { EstablishmentSwitcher } from './EstablishmentSwitcher';
import logoMark from '@/images/Resstrologo.png';

const nav: Array<{
  to: string;
  label: string;
  icon: LucideIcon;
  live?: boolean;
  ownerOnly?: boolean;
}> = [
  { to: '/', label: 'Главная', icon: LayoutDashboard },
  { to: '/current-shift', label: 'Текущая касса', icon: Radio, live: true },
  { to: '/users', label: 'Пользователи', icon: Users2 },
  { to: '/menu', label: 'Меню', icon: UtensilsCrossed },
  { to: '/orders', label: 'Заказы', icon: ShoppingBasket },
  { to: '/analytics', label: 'Аналитика', icon: BarChart3 },
  { to: '/expenses', label: 'Расходы', icon: Receipt },
  { to: '/shifts', label: 'Смены', icon: CalendarClock },
  { to: '/reports', label: 'Отчёты', icon: FileBarChart2 },
  { to: '/warehouse', label: 'Склад', icon: Warehouse },
  { to: '/devices', label: 'Устройства', icon: Tablet },
  { to: '/subscription', label: 'Подписка и оплата', icon: CreditCard, ownerOnly: true },
  { to: '/app', label: 'Приложение', icon: Smartphone },
  { to: '/settings', label: 'Настройки', icon: Settings },
];

export function Sidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const resetEstablishment = useEstablishmentStore((s) => s.setCurrentId);
  const navigate = useNavigate();

  return (
    <aside className="h-full w-full bg-white border-r border-slate-100 flex flex-col">
      <div className="px-5 py-5 border-b border-slate-100 flex items-center gap-3">
        <img
          src={logoMark}
          alt="Resstro"
          className="w-9 h-9 rounded-lg object-cover shrink-0"
        />
        <div className="leading-tight">
          <div className="font-semibold">Resstro SAS</div>
          <div className="text-xs text-slate-400">админ-панель</div>
        </div>
      </div>

      <EstablishmentSwitcher />

      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-auto">
        {nav
          .filter((n) => !n.ownerOnly || user?.role === 'OWNER')
          .map(({ to, label, icon: Icon, live }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={() => onNavigate?.()}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                isActive
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`
            }
          >
            <Icon size={16} />
            <span className="flex-1">{label}</span>
            {live && (
              <span className="relative inline-flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-slate-100">
        <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-slate-50">
          <div className="w-8 h-8 rounded-full bg-brand-200 text-brand-800 flex items-center justify-center text-xs font-bold">
            {(user?.name ?? user?.email ?? '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name ?? user?.email ?? '—'}</div>
            <div className="text-xs text-slate-500 truncate">
              {user ? roleLabels[user.role] : ''}
            </div>
          </div>
          <button
            onClick={() => {
              logout();
              resetEstablishment(null);
              navigate('/login');
            }}
            className="p-1.5 rounded hover:bg-white text-slate-500"
            title="Выйти"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}
